import { mapperPermissionsList } from 'utils/permissions';

const FilterSchema = {
    userName: {
        dataType: 'text',
        description: 'cfm_label_name',
        id: 'userName',
    },
    externalId: {
        dataType: 'text',
        description: 'cfm_label_external_id',
        id: 'extId',
    },
    userEmail: {
        dataType: 'text',
        description: 'cfm_label_email',
        id: 'userEmail',
    },
    userPhone: {
        dataType: 'text',
        description: 'cfm_label_mobile',
        id: 'userPhone',
    },
    userEnvironment: {
        dataType: 'text',
        description: 'cfm_label_home_environment',
        id: 'userEnvironment',
    },
    responsibleName: {
        dataType: 'text',
        description: 'cfm_label_responsible',
        id: 'responsibleName',
    },
    isActive: {
        dataType: 'singleValueList',
        description: 'cfm_label_users_status',
        id: 'isActive',
        options: 'isActiveOptions',
    },

    // isBillable
};

const CrudFields = [
    'computeInSfm',
    'email',
    'environment',
    'externalId',
    'extraFields',
    'isActive',
    'isBillable',
    'languageDB',
    'languageUI',
    'lastName',
    'locale',
    'name',
    'nic',
    'password',
    'idPrefix',
    'phone',
    'phoneExtension',
    'photo',
    'responsibleId',
    'rolesAssociated',
    'setupLogin',
    'timeZoneId',
    'userTypeId',
    'idSetupRole',
    'is2FAUserEnabled',
];

export default class UsersManager {
    constructor(api) {
        this.api = api;
    }

    getFilterSchema = () => FilterSchema;

    getCrudFields = () => CrudFields;

    getImplSummary = () => this.api.getImplSummary();

    getUsers = (data) =>
        this.api.getUsers(data).then((res) => {
            const result = this.mapperUsersList(res);
            return result;
        });

    getUsersHierachy = () => this.api.getUsersHierachy();

    getUser = (id) => this.api.getUser(id);

    getDuplicateUser = (id) =>
        this.api.getUser(id).then((user) => {
            const result = this.mapperDuplicateUser(user);
            return result;
        });

    getUserPermissions = (userId) =>
        this.api.getUserPermissions(userId).then((res) => {
            const result = mapperPermissionsList(res);
            return result;
        });

    getUserPermissionsDraft = (roles) =>
        this.api.getUserPermissionsDraft(roles).then((res) => {
            const result = mapperPermissionsList(res);
            return result;
        });

    getRoleByPermissionId = (id) => this.api.getRoleByPermissionId(id);

    getRoleByPermissionAndUserId = (id, userId) =>
        this.api.getRoleByPermissionAndUserId(id, userId);

    toggleActivateUser = (id, isUserActive) =>
        this.api.toggleActivateUser(id, {
            isUserActive,
        });

    getPasswordPolicies = () => this.api.getPasswordPolicies();

    getSSOProvider = () => this.api.getSSOProvider();

    getUserSchema = () => this.api.getCrudSchema('tblUsuarios');

    getInitialCrudValues = (user) => {
        const obj = user || {};
        const { setupRole } = obj;
        const finalSetupRole = setupRole
            ? {
                  label: setupRole?.value,
                  value: setupRole?.id,
                  readOnly: setupRole?.readOnly,
              }
            : {};

        return {
            computeInSfm: obj.computeInSfm !== undefined ? obj.computeInSfm : true,
            email: obj.email || '',
            environment:
                obj.branch && obj.branch.id > 0
                    ? { value: obj.branch.id, label: obj.branch.description }
                    : null,
            externalId: obj.extId || '',
            extraFields: obj.extraFields ? this.mapperExtraFields(obj.extraFields) : {},
            isActive: obj.isActive !== undefined ? obj.isActive : true,
            isBillable: obj.isBillable !== undefined ? obj.isBillable : true,
            languageDB: obj.languageDB || 'ES',
            languageUI: obj.languageUI || 'ES',
            lastName: obj.lastName || '',
            locale: obj.locale || 'es-ES',
            name: obj.name || '',
            nic: obj.nic || '',
            password: obj.password || '',
            idPrefix: obj.idPrefix || null,
            phone: obj.phone || '',
            phoneExtension: obj.phoneExtension || '',
            photo: obj.photo || '',
            responsibleId:
                obj.responsible && obj.responsible.id > 0
                    ? { value: obj.responsible.id, label: obj.responsible.value }
                    : null,
            rolesAssociated: obj.rolesAssociated
                ? obj.rolesAssociated.map((role) => role.userRoleId)
                : [],
            setupLogin: obj.setupLogin || false,
            timeZoneId: obj.timeZone && obj.timeZone.id > 0 ? obj.timeZone.id : 31,
            userTypeId: obj.userType && obj.userType.id > 0 ? obj.userType.id : -1,
            idSetupRole: obj?.setupRole?.id > 0 ? finalSetupRole : null,
            is2FAUserEnabled: obj.is2FAUserEnabled !== undefined ? obj.is2FAUserEnabled : true,
        };
    };

    mapperExtraFields = (extrafields) => {
        const result = {};
        Object.keys(extrafields).forEach((ef) => {
            const value = extrafields[ef];
            if (value !== undefined && value !== null) {
                if (typeof value === 'object' && value.id > 0) {
                    // type list
                    result[ef] = value.id;
                } else if (Array.isArray(value)) {
                    // type multivalue
                    result[ef] = value.map((v) => v.id);
                } else {
                    result[ef] = value;
                }
            }
        });
        return result;
    };

    mapperDuplicateUser = (user) => ({
        ...user,
        email: '',
        externalId: '',
        id: -1,
        name: '',
        lastName: '',
        nic: '',
        password: '',
        phone: '',
        phoneExtension: '',
        photo: '',
    });

    mapperUsersList = (users) =>
        users.map((user) => ({
            ...user,
            userEnvironment:
                user.environment && user.environment.id > 0 ? user.environment.name : '',
            responsibleName:
                user.responsible && user.responsible.id > 0 ? user.responsible.value : '',
        }));

    createUser = ({ values }) => this.api.createUser(values);

    updateUser = ({ id, values }) => this.api.updateUser(id, values);

    deleteUser = (id) => this.api.deleteUser(id);

    sendEmailPassword = (id) => this.api.sendEmailPassword(id);

    getCountryCodes = () => this.api.getCountryCodes();
}
